<script setup lang="ts">
import {computed, onMounted, PropType, ref} from "vue";
import {voteSavedOffer} from "@/api";
import {getSavedOffers, } from "@/services/trip.service";
import {Trip} from "@/models/Trip";
import {SavedOffer} from "@/models/SavedOffer";
import OfferCalendar from "@/components/TripSavedOffers/OfferCalendar.vue";
import { useI18n } from "vue-i18n";
import {useUserStore} from "@/stores/userStore";

const { t } = useI18n(); // Use the i18n instance
const userStore = useUserStore();

const props = defineProps({
  trip: {
    type: Object as PropType<Trip>,
    required: true
  },
})


const offers = ref<SavedOffer[]>([]);

const sort = ref<Record<string, 'asc' | 'desc'>>({})
const date = ref(new Date().setHours(0));
const offersAvailableForDate = computed(() => {
  return offers.value.filter(offer =>
    new Date(offer.offer.availability.from).getTime() <= date.value &&
    new Date(offer.offer.availability.to).getTime() >= date.value
  );
});


function vote(event, offerId: string) {
  voteSavedOffer(props.trip.uuid, offerId)

  let likeBtn = event.currentTarget;
  // console.log(likeBtn.classList.includes('liked'));
  if (!likeBtn.classList.contains('liked')) {
    likeBtn.classList.add('liked');
  } else {
    likeBtn.classList.remove('liked');
  }
}

const showPriceCalendar = ref(false);

onMounted(() => {
  if (props.trip && offers.value.length === 0) {
    getSavedOffers(props.trip.uuid).then(data => {
      offers.value = data.data;
    }).catch(err => {
      console.error(err)
    })
  }
})

function onSortClick(portId: string) {
  if (portId in sort.value) {
    sort.value[portId] = sort.value[portId] === 'asc' ? 'desc' : 'asc';
  } else {
    sort.value[portId] = 'desc';
  }
}
</script>

<template>
  <div class="container flex bg-primary-ciel rounded-2xl h-20">
    <div class="px-5 flex w-full justify-between items-center">
      <div class="availability-dates flex gap-12 h-full justify-between items-center w-1/2">
        <div>
          <span class="font-bold">{{ t('availability') }}</span>
        </div>
        <span class="cursor-pointer" @click="date = date - 24 * 1000 * 60 * 60"> {{ '<' }} </span>
            <div v-for="i in [-1, 0, 1]" :key="i"
              :class="`availability-date__text flex flex-col items-center w-4/5 cursor-pointer ${date === (date + i * 24 * 1000 * 60 * 60) ? 'border-b-4 border-black pb-6 mt-auto' : ''}`"
              @click="date = date + i * 24 * 1000 * 60 * 60">
              <span>{{ userStore.formatDate(new Date(date + i * 24 * 1000 * 60 * 60)) }}</span>
              <!-- User's preferred format -->
            </div>
            <span class="cursor-pointer" @click="date = date + 24 * 1000 * 60 * 60"> > </span>
      </div>
      <button class="bg-black text-white rounded-11xl px-5 py-2" @click="showPriceCalendar = true">{{
            t('priceCalendar') }}</button>
    </div>
  </div>

  <div v-for="offer in offersAvailableForDate" :key="offer.offer.uuid">
    <div class="saved-offer-result" v-for="port in offer.ports" :key="port.uuid">
      <div class="container flex mt-12 mb-8">
        <div class="flex justify-between w-full">
          <div class='location-count flex gap-2.5 items-center'>
            <span class="font-bold flex flex-row items-center">
              <img src="@/assets/icons/svg/Location_24x24.svg" alt="location" />
              {{ port.port }}
            </span>
            <span class="responsive-body-xxsmall">{{ port.boats.length }} {{ $t('boats') }}</span>
          </div>
          <div class="flex flex-row gap-5">
            <div class="cursor-pointer flex flex-row items-center" @click="onSortClick(port.uuid)">
              <span class="text-sm">{{ $t('price') }}
                ({{ (sort[port.uuid] ?? 'asc') === 'asc' ? $t('lowToHigh') : $t('highToLow') }})
              </span>
              <img v-if="(sort[port.uuid] ?? 'asc') === 'asc'" class="ml-2 h-3.5"
                src="@/assets/icons/svg/Down_16x16.svg" alt="Low to High">
              <img v-else class="ml-2" src="@/assets/icons/svg/Up_16x16.svg" alt="High to Low">
            </div>
            <div class="border border-black px-5 rounded-12xl flex flex-row items-center">
              <span class="text-sm">{{ $t('topPicks') }}</span>
              <img class="ml-2 h-3.5" src="@/assets/icons/svg/Down_16x16.svg" alt="dropdown">
            </div>
          </div>
        </div>
      </div>
      <div class="container saved-offer-results grid grid-cols-4 gap-5">
        <div v-for="boat in port.boats.sort((a, b) => {
            if (!(port.uuid in sort) || sort[port.uuid] === 'asc') {
              return a.pricing.newPrice - b.pricing.newPrice;
            } else {
              return b.pricing.newPrice - a.pricing.newPrice;
            }
          })" :key="boat.uuid">
          <div class="saved-offer-content border rounded-8xl flex flex-col gap-1">
            <div class="flex flex-col gap-3 bg-gradient-to-r from-gradients-ghostWhite to-gradients-aliceBlue rounded-8xl p-5">
              <div class="flex flex-row justify-between ">
                <span class='font-bold'>{{ boat.name }}</span>
                <span><img src="@/assets/icons/svg/Delete_16x16.svg" alt="{{ $t('delete') }}"></span>
              </div>
              <div>
                <img class="h-40px] object-cover w-full" src="@/assets/images/boat.png" alt="Boat Image" />
              </div>
              <div class="flex flex-row justify-between">
                <div class="flex flex-col items-center">
                  <img src="@/assets/icons/svg/Bed_24x24.svg" alt="{{ $t('beds') }}" />
                  <span class="responsive-body-xxsmall">{{ boat.specs.beds }} {{ $t('beds') }}</span>
                </div>
                <div class="flex flex-col items-center">
                  <img src="@/assets/icons/svg/Cabin_24x24.svg" alt="{{ $t('cabins') }}" />
                  <span class="responsive-body-xxsmall">{{ boat.specs.cabins }} {{ $t('cabins') }}</span>
                </div>
                <div class="flex flex-col items-center">
                  <img src="@/assets/icons/svg/WC_24x24.svg" alt="{{ $t('wc') }}" />
                  <span class="responsive-body-xxsmall">{{ boat.specs.wc }} {{ $t('wc') }}</span>
                </div>
                <div class="flex flex-col items-center">
                  <img src="@/assets/icons/svg/Length_24x24.svg" alt="{{ $t('length') }}" />
                  <span class="responsive-body-xxsmall">{{ boat.specs.length }}</span>
                </div>
                <div class="flex flex-col items-center">
                  <img src="@/assets/icons/svg/Engine_24x24.svg" alt="{{ $t('engine') }}" />
                  <span class="responsive-body-xxsmall">{{ boat.specs.engine }}</span>
                </div>
              </div>
            </div>

            <div v-if="boat.pricing.newPrice" class="flex flex-row gap-1 px-4 items-center">
              <span class="responsive-body-xsmall text-grey line-through">€{{ $n(boat.pricing.oldPrice,'decimal') }}</span>
              <span class="responsive-body-xsmall text-primary-blue">{{ $t('specialOffer') }}</span>
            </div>
            <div class="flex flex-row gap-1 px-4 items-center">
              <span class="responsive-title-4 font-semibold">€{{ $n(boat.pricing.newPrice,'decimal') }}</span>
              <span class="responsive-body-xsmall"> {{ $t('priceFor') }} {{ boat.duration }}</span>
            </div>
            <div class="flex flex-row items-center justify-between px-3 py-2">
              <button class="flex flex-row items-center gap-1 bg-black text-white rounded-12xl px-5 py-2">
                <img class="white-arrow w-4" src="@/assets/icons/svg/Down_White_24x24.svg"
                  alt="{{ $t('request') }}" />
                <span class=" responsive-body-small font-semibold">{{ $t('request') }}</span>
              </button>
              <ElTooltip popper-class="pointer-events-none" placement="bottom" effect="light">
                <template #content>
                  <div class="text-center">{{ $t('votedBy') }}</div>
                  <div>{{ boat.votes?.map(vote => vote.user.name).join(', ') ?? '' }}</div>
                </template>
                <button @click="vote($event, boat.uuid)"
                  class="flex flex-row items-center gap-1 bg-transparent border border-grey rounded-12xl px-5 py-1">
                  <img src="@/assets/icons/svg/Thumbs_up_16x16.svg" alt="{{ $t('vote') }}" />
                  <span>{{ boat.votes.length }}</span>
                </button>
              </ElTooltip>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <OfferCalendar :show="showPriceCalendar" :id="trip.uuid" @on-close="showPriceCalendar = false" />
</template>

<style scoped>
.mySticky {
  position: sticky;
  bottom: 0;
  z-index: 1000;
}

.border-black {
  border-color: black !important;
}

:deep(.modal-content) {
  padding: 0;
}

.width-fit-content {
  width: fit-content;
}

.saved-offer-content .liked {
  background-color: #1B84FF !important;
  color: white !important;
}

.white-arrow {
  fill: white;
}

:deep(.modal-content) {
  padding: 0;
}

button.liked img {
  filter: invert(1);
}

.absolute {
  z-index: 1000;
}

.saved-offer-result:not(:last-child) {
  border-bottom-width: 1px;
  padding-bottom: 3rem;
}

@media (min-width: 1538px) {
  .saved-offer-results {
    grid-template-columns: repeat(5, minmax(0, 1fr));
  }
}

@media (max-width: 1375px) {
  .availability-dates {
    width: 60%;
  }
  .availability-date__text {
    font-size: .875rem;
    width: 15%;
  }
}

@media (max-width: 1280px) {
  .saved-offer-results {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}
@media (max-width: 1155px) {
  .availability-dates {
    width: 78%;
  }
}
@media (max-width: 965px) {
  .saved-offer-results {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

}

@media (max-width: 885px) {
  .availability-dates {
    width: 75%;
    column-gap: 30px;
  }
}

@media (max-width: 645px) {
  .saved-offer-results {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }
  .location-count {
    flex-direction: column;
  }
}
</style>
